/**
 * BoardFallback Component
 * Displays a calm, professional fallback screen when the flight board is unavailable
 * Shows last-known-good data if available, otherwise shows a reconnecting message
 */

import { useEffect } from 'react';
import { BlankReason } from '../services/flightDataMonitor.js';

// Styles - Table area replacement (not full screen overlay)
// This component replaces only the table area, preserving ads and announcements
const containerStyle = {
  // Fills the table container area (flex child)
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  backgroundColor: '#000000',
  display: 'flex',
  flexDirection: 'column',
  fontFamily: 'Arial, Helvetica, sans-serif',
  color: '#ffffff',
  boxSizing: 'border-box'
};

// Status banner at top of fallback area
const statusBannerStyle = {
  padding: '10px 20px',
  backgroundColor: '#1a1a1a',
  borderBottom: '2px solid #FFD200',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: '14px',
  color: '#888888',
  flexShrink: 0
};

const mainContentStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px'
};

const messageStyle = {
  fontSize: '36px',
  fontWeight: 500,
  textAlign: 'center',
  marginBottom: '30px',
  color: '#FFD200'
};

const subMessageStyle = {
  fontSize: '24px',
  textAlign: 'center',
  color: '#888888',
  marginBottom: '40px'
};

const lastUpdateStyle = {
  fontSize: '18px',
  color: '#666666',
  marginTop: '20px'
};

const spinnerContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '30px'
};

const spinnerStyle = {
  width: '50px',
  height: '50px',
  border: '4px solid #333333',
  borderTop: '4px solid #FFD200',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite'
};

const tableContainerStyle = {
  flex: 1,
  overflow: 'auto',
  padding: '20px'
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '20px'
};

const thStyle = {
  backgroundColor: '#1a1a1a',
  padding: '15px 10px',
  textAlign: 'left',
  borderBottom: '2px solid #333333',
  color: '#FFD200'
};

const tdStyle = {
  padding: '12px 10px',
  borderBottom: '1px solid #222222'
};

// CSS animation for spinner
const spinKeyframes = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

/**
 * Get user-friendly message based on blank reason
 */
function getReasonMessage(reason) {
  switch (reason) {
    case BlankReason.EMPTY:
      return 'No scheduled flights at this time';
    case BlankReason.FILTERED_OUT:
      return 'Flight information temporarily unavailable';
    case BlankReason.NETWORK_STALE:
      return 'Reconnecting to flight data...';
    case BlankReason.BROKEN_UI:
    case BlankReason.WEBVIEW_FREEZE:
      return 'Display refreshing...';
    default:
      return 'Flight information temporarily unavailable';
  }
}

/**
 * Get sub-message with more detail
 */
function getSubMessage(reason) {
  switch (reason) {
    case BlankReason.EMPTY:
      return 'Please check departure/arrival screens for other flights';
    case BlankReason.NETWORK_STALE:
      return 'Attempting to restore connection';
    default:
      return 'Service will resume automatically';
  }
}

/**
 * Format time for display
 */
function formatTime(date = new Date()) {
  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Africa/Lagos'
  });
}

/**
 * Format flight time
 */
function formatFlightTime(isoString) {
  if (!isoString) return '--:--';
  try {
    const d = new Date(isoString);
    return d.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Africa/Lagos'
    });
  } catch {
    return '--:--';
  }
}

/**
 * BoardFallback Component
 */
export function BoardFallback({
  boardType = 'arrivals',
  reason = BlankReason.NONE,
  lastKnownGood = null,
  recoveryLevel = 0,
  deviceId = null
}) {
  // Inject spinner animation
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.textContent = spinKeyframes;
    document.head.appendChild(styleEl);
    return () => document.head.removeChild(styleEl);
  }, []);

  const hasCache = lastKnownGood && lastKnownGood.flights && lastKnownGood.flights.length > 0;

  // Get device ID from localStorage if not provided
  const displayDeviceId = deviceId || localStorage.getItem('fids_device_id') || 'Unknown';

  return (
    <div style={containerStyle} className="board-fallback">
      {/* Status banner - shows cached data info or recovery status */}
      <div style={statusBannerStyle}>
        <span style={{ color: '#FFD200' }}>
          {hasCache
            ? `Showing last update from ${lastKnownGood.formattedTime || formatTime(new Date(lastKnownGood.timestamp))}`
            : getReasonMessage(reason)
          }
        </span>
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          Level {recoveryLevel} | {displayDeviceId.slice(-8)}
        </span>
      </div>

      {/* Main content */}
      {hasCache ? (
        // Show cached flight data
        <div style={tableContainerStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Airline</th>
                <th style={thStyle}>Flight</th>
                <th style={thStyle}>{boardType === 'arrivals' ? 'Origin' : 'Destination'}</th>
                <th style={thStyle}>Time</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {lastKnownGood.flights.slice(0, 15).map((flight, idx) => (
                <tr key={flight.key || idx} style={{ backgroundColor: idx % 2 === 0 ? '#1a1a1a' : '#000000' }}>
                  <td style={tdStyle}>{flight.airlineName || flight.airlineCode}</td>
                  <td style={tdStyle}>{flight.flight}</td>
                  <td style={tdStyle}>{flight.displayname || '--'}</td>
                  <td style={tdStyle}>{formatFlightTime(flight.estimatedTime || flight.scheduledTime)}</td>
                  <td style={tdStyle}>
                    <span style={{
                      color: flight.status?.toLowerCase().includes('delay') ? '#ff6b6b' :
                             flight.status?.toLowerCase().includes('landed') ? '#51cf66' :
                             flight.status?.toLowerCase().includes('departed') ? '#51cf66' :
                             '#FFD200'
                    }}>
                      {flight.status || '--'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        // Show reconnecting message
        <div style={mainContentStyle}>
          <div style={spinnerContainerStyle}>
            <div style={spinnerStyle} />
          </div>
          <div style={messageStyle}>{getReasonMessage(reason)}</div>
          <div style={subMessageStyle}>{getSubMessage(reason)}</div>
          {lastKnownGood?.timestamp && (
            <div style={lastUpdateStyle}>
              Last successful update: {lastKnownGood.formattedTime}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default BoardFallback;
